import React from 'react';
import _ from 'lodash';
import * as SiteManager from '../domain/site-manager';
import { useData, dataFromDataset } from './DataProviderCache';
import { ChannelDatum } from '../../types';
import { useSiteDevicesData } from './SiteDevicesDataProvider';
import { useAuthHelper } from '../../components/AuthContextProvider';
import { isDataLoaded, ExtractDataType, makeLoadedData, Data } from '../../types/data-providers';

type ContextState = Data<ChannelDatum[]>;

const Context = React.createContext<ContextState | null>(null);

function useSiteChannelData(): ContextState {
    const contextState = React.useContext(Context);
    if (contextState === null) {
        throw new Error('useSiteChannelData must be used within a SiteChannelDataProvider');
    }
    return contextState;
}

const SiteChannelDataProvider: React.FC<{ siteId: string; limit?: number; priority?: boolean }> = (props) => {
    const authHelper = useAuthHelper();
    const devicesData = useSiteDevicesData();

    const { dataset } = useData({
        cacheKey: 'site-channel-data',
        id: props.siteId,
        dataFn: React.useCallback(
            () =>
                SiteManager.getChannelData(authHelper, {
                    siteId: props.siteId,
                    limit: props.limit,
                    priority: props.priority,
                }),
            [authHelper, props.siteId, props.limit, props.priority]
        ),
    });
    const channelData = dataFromDataset(dataset);
    const value = React.useMemo(() => {
        if (!isDataLoaded(devicesData)) {
            return devicesData;
        }

        if (!isDataLoaded(channelData)) {
            return channelData;
        }

        return makeLoadedData<ExtractDataType<ContextState>>(
            channelData.value
                .filter((channelDatum) => !_.isNil(channelDatum.channelName) && !_.isNil(channelDatum.value))
                .map((channelDatum) => ({
                    channelName: channelDatum.channelName || '',
                    deviceId: channelDatum.deviceId,
                    deviceName: channelDatum.deviceName,
                    value: channelDatum.value || undefined,
                    units: channelDatum.units || '',
                    id: channelDatum.id,
                    iconClass: channelDatum.iconClass,
                }))
        );
    }, [devicesData, channelData]);

    return <Context.Provider value={value}>{props.children}</Context.Provider>;
};

const TestSiteChannelDataProvider: React.FC<{ value: ContextState }> = (props) => (
    <Context.Provider value={props.value}>{props.children}</Context.Provider>
);

export { SiteChannelDataProvider, TestSiteChannelDataProvider, useSiteChannelData };
